import { DomainError, SyncError, TransportError, } from "@econ-v1/domain";
import { KernelEventBus } from "../bus/event-bus.js";
import { ReplayLock } from "./replay-lock.js";
export class OutboxRunner {
    #authoritativeDomains;
    #authoritativePreflight;
    #bus;
    #clock;
    #delivery;
    #leaseMs;
    #maxAttempts;
    #outbox;
    #scheduler;
    #schedulerCadenceMs;
    #attempts = new Map();
    #lock;
    #conflicts = [];
    #leaseSequence = 0;
    #unsubscribeTransport;
    constructor(options) {
        this.#authoritativeDomains = new Set(options.authoritativeDomains ?? []);
        this.#authoritativePreflight = options.authoritativePreflight;
        this.#bus = options.bus;
        this.#clock = options.clock;
        this.#delivery = options.delivery;
        this.#leaseMs = options.leaseMs ?? 30_000;
        this.#maxAttempts = options.maxAttempts ?? 3;
        this.#outbox = options.outbox;
        if (options.requireAtomicReplay === true &&
            (options.outbox.claimHead === undefined || options.outbox.settle === undefined)) {
            throw new DomainError({
                code: "conflict",
                message: "Durable outbox replay requires atomic claim and settlement support",
            });
        }
        this.#scheduler = options.scheduler;
        this.#schedulerCadenceMs = options.schedulerCadenceMs ?? 30_000;
        this.#lock = new ReplayLock({ leaseMs: this.#leaseMs, now: () => this.#clock.now() });
    }
    start() {
        if (this.#unsubscribeTransport !== undefined)
            return;
        this.#unsubscribeTransport = this.#bus.subscribe("transport.state-changed", (event) => {
            if (event.payload.state.status === "connected")
                void this.runOnce();
        });
        this.#scheduler?.register({
            cadenceMs: this.#schedulerCadenceMs,
            dueAt: this.#clock.now(),
            name: "sync.outbox-replay",
            run: async () => { await this.runOnce(); },
        });
    }
    stop() {
        this.#unsubscribeTransport?.();
        this.#unsubscribeTransport = undefined;
        this.#scheduler?.cancel("sync.outbox-replay");
    }
    async runOnce() {
        const records = await this.#outbox.list();
        const domains = [...new Set(records.map((record) => record.domain))];
        const results = await Promise.all(domains.map(async (domain) => await this.#runDomain(domain)));
        return results.flat();
    }
    async cancel(id) {
        const current = await this.#outbox.read(id);
        if (current === undefined)
            return false;
        if (current.state !== "pending")
            return false;
        await this.#outbox.delete(id);
        return true;
    }
    parkedConflicts() {
        return [...this.#conflicts];
    }
    async #runDomain(domain) {
        const records = await this.#outbox.list({ domain });
        const head = firstNonTerminal(records);
        if (head === undefined)
            return [];
        const lease = this.#lock.acquire(domain, head.id);
        if (lease === undefined)
            return [];
        const results = [];
        try {
            for (;;) {
                const current = firstNonTerminal(await this.#outbox.list({ domain }));
                if (current === undefined)
                    return results;
                if (this.#outbox.claimHead !== undefined && this.#outbox.settle !== undefined) {
                    return await this.#runAtomicDomain(domain, results);
                }
                const now = this.#clock.now();
                if (current.state === "replaying") {
                    if (Date.parse(current.updatedAt) + this.#leaseMs > Date.parse(now))
                        return results;
                    await this.#outbox.updateState(current.id, "pending");
                }
                const result = await this.#deliver(current);
                results.push(result);
                if (result.status === "failed" || result.status === "conflict")
                    return results;
            }
        }
        finally {
            this.#lock.release(domain, lease.token);
        }
    }
    async #runAtomicDomain(domain, results) {
        for (;;) {
            const leaseToken = `runner-${++this.#leaseSequence}`;
            const now = this.#clock.now();
            const claim = await this.#outbox.claimHead({
                domain,
                leaseExpiresAt: new Date(Date.parse(now) + this.#leaseMs).toISOString(),
                leaseToken,
            });
            if (claim === undefined)
                return results;
            const result = await this.#deliverAtomic(claim);
            results.push(result);
            if (result.status !== "acknowledged")
                return results;
        }
    }
    async #deliverAtomic(claim) {
        const settle = async (input) => {
            await this.#outbox.settle(input);
        };
        try {
            if (this.#authoritativeDomains.has(claim.domain)) {
                const preflight = this.#authoritativePreflight === undefined
                    ? { allowed: false, reason: "fresh state required" }
                    : await this.#authoritativePreflight();
                if (!preflight.allowed) {
                    await settle({
                        error: { code: "sync_revalidation_required", message: preflight.reason ?? "fresh state required" },
                        id: claim.id,
                        leaseToken: claim.leaseToken,
                        outcome: "conflict",
                    });
                    const conflict = { ...claim, state: "conflict" };
                    this.#conflicts.push(conflict);
                    return { attempts: claim.attemptCount, domain: claim.domain, id: claim.id, status: "conflict" };
                }
            }
            await this.#delivery(claim);
            await settle({ id: claim.id, leaseToken: claim.leaseToken, outcome: "acknowledged" });
            return { attempts: claim.attemptCount, domain: claim.domain, id: claim.id, status: "acknowledged" };
        }
        catch (error) {
            const conflict = isConflict(error);
            const retryable = isRetryable(error);
            const errorInfo = { code: errorCode(error), message: errorMessage(error) };
            if (conflict) {
                await settle({ error: errorInfo, id: claim.id, leaseToken: claim.leaseToken, outcome: "conflict" });
                this.#conflicts.push({ ...claim, state: "conflict" });
                return { attempts: claim.attemptCount, domain: claim.domain, id: claim.id, status: "conflict" };
            }
            if (retryable && claim.attemptCount < this.#maxAttempts) {
                await settle({
                    error: errorInfo,
                    id: claim.id,
                    leaseToken: claim.leaseToken,
                    outcome: "retry",
                    retryAt: new Date(Date.parse(this.#clock.now()) + claim.attemptCount * 1_000).toISOString(),
                });
                return await this.#deliverAtomicRetry(claim.domain);
            }
            await settle({ error: errorInfo, id: claim.id, leaseToken: claim.leaseToken, outcome: "failed" });
            return { attempts: claim.attemptCount, domain: claim.domain, id: claim.id, status: "failed" };
        }
    }
    async #deliverAtomicRetry(domain) {
        const claim = await this.#outbox.claimHead({
            domain,
            leaseExpiresAt: new Date(Date.parse(this.#clock.now()) + this.#leaseMs).toISOString(),
            leaseToken: `runner-${++this.#leaseSequence}`,
        });
        if (claim === undefined)
            return { attempts: 0, domain, id: "", status: "pending" };
        return await this.#deliverAtomic(claim);
    }
    async #deliver(intent) {
        if (this.#authoritativeDomains.has(intent.domain)) {
            const preflight = this.#authoritativePreflight === undefined
                ? { allowed: false, reason: "fresh state required" }
                : await this.#authoritativePreflight();
            if (!preflight.allowed) {
                await this.#outbox.updateState(intent.id, "failed");
                const conflict = { ...intent, state: "failed" };
                this.#conflicts.push(conflict);
                return { attempts: 0, domain: intent.domain, id: intent.id, status: "conflict" };
            }
        }
        const attempts = (this.#attempts.get(intent.id) ?? 0) + 1;
        this.#attempts.set(intent.id, attempts);
        await this.#outbox.updateState(intent.id, "replaying");
        try {
            await this.#delivery(intent);
            await this.#outbox.updateState(intent.id, "acknowledged");
            return { attempts, domain: intent.domain, id: intent.id, status: "acknowledged" };
        }
        catch (error) {
            const conflict = isConflict(error);
            const retryable = isRetryable(error);
            if (conflict) {
                await this.#outbox.updateState(intent.id, "failed");
                const parked = { ...intent, state: "failed" };
                this.#conflicts.push(parked);
                return { attempts, domain: intent.domain, id: intent.id, status: "conflict" };
            }
            if (retryable && attempts < this.#maxAttempts) {
                await this.#outbox.updateState(intent.id, "pending");
                return await this.#deliver({ ...intent, state: "pending" });
            }
            await this.#outbox.updateState(intent.id, "failed");
            return { attempts, domain: intent.domain, id: intent.id, status: "failed" };
        }
    }
}
function firstNonTerminal(records) {
    return [...records]
        .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))
        .find((record) => record.state !== "acknowledged" &&
        record.state !== "failed" &&
        record.state !== "conflict");
}
function isRetryable(error) {
    return error instanceof TransportError || error instanceof SyncError;
}
function isConflict(error) {
    return error instanceof DomainError && error.code === "conflict";
}
function errorCode(error) {
    if (error instanceof DomainError || error instanceof SyncError || error instanceof TransportError)
        return error.code;
    return "outbox_delivery_failed";
}
function errorMessage(error) {
    return error instanceof Error ? error.message : "Outbox delivery failed";
}
//# sourceMappingURL=outbox-runner.js.map